import { useEffect, useState } from "react";
import { Loader2, Lock, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { money } from "@/lib/plan";

export function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((res) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

const Row = ({ l, v, testId, bold }) => (
  <div className={`flex justify-between py-1.5 text-sm ${bold ? "border-t border-line pt-3 font-bold text-ink" : "text-ink-2"}`}><span>{l}</span><span data-testid={testId} className="font-mono">{v}</span></div>
);

export function CheckoutDialog({ plan, cycle, currency, paymentsEnabled, testMode, onClose, onDone }) {
  const { user, setUser } = useAuth();
  const [code, setCode] = useState("");
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { setCode(""); setDone(null); setQ(null); if (plan) api.post("/billing/quote", { plan_id: plan.id, cycle }).then((r) => setQ(r.data)).catch((e) => toast.error(formatError(e))); }, [plan, cycle, currency]);

  const apply = async () => {
    try { setQ((await api.post("/billing/quote", { plan_id: plan.id, cycle, code })).data); toast.success("Coupon applied"); } catch (e) { toast.error(formatError(e)); }
  };
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: plan.id, cycle, code: q?.coupon || null });
      if (data.status !== "order") { setDone(data.message); setBusy(false); return; }
      if (!(await loadRazorpay())) throw new Error("Couldn't load the payment window. Check your connection and try again.");
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: data.currency, name: "IDDCR LearnAI",
        description: `${data.plan_name} (${data.cycle})`, prefill: data.prefill, theme: { color: "#2878F0" },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", resp);
            setUser((await api.get("/auth/me")).data);
            toast.success(`${data.plan_name} is now active!`); onDone();
          } catch (e) { toast.error(formatError(e)); }
          setBusy(false);
        },
      });
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed. You have not been charged."); setBusy(false); });
      onClose();
      rzp.open();
    } catch (e) { toast.error(formatError(e)); setBusy(false); }
  };

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle>{done ? `${plan?.name} is coming soon` : `${user?.plan === plan?.id ? "Renew" : "Upgrade to"} ${plan?.name}`}</DialogTitle>
          <DialogDescription data-testid="checkout-dialog-message">{done || `Billed ${cycle}. One-time payment, no auto-renewal. We'll remind you before it ends.`}</DialogDescription>
        </DialogHeader>
        {!done && (q ? (
          <div className="grid gap-4">
            <div className="flex gap-2">
              <div className="relative flex-1"><Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
                <Input data-testid="coupon-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="pl-9 uppercase" /></div>
              <Button data-testid="coupon-apply-btn" variant="outline" disabled={!code.trim()} onClick={apply}>Apply</Button>
            </div>
            <div className="rounded-xl bg-canvas-2 p-4">
              <Row l={`${q.plan_name} · ${cycle}`} v={money(q.base, q.currency)} testId="checkout-base" />
              {q.coupon && <Row l={`Coupon ${q.coupon}`} v={`−${money(q.discount, q.currency)}`} testId="checkout-discount" />}
              <Row l="Total" v={money(q.amount, q.currency)} testId="checkout-total" bold />
            </div>
            <Button data-testid="checkout-pay-btn" disabled={busy} onClick={pay} className="h-11 rounded-full">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
              {paymentsEnabled ? `Pay ${money(q.amount, q.currency)} securely` : "Notify me when payments open"}
            </Button>
            {paymentsEnabled && <p data-testid="checkout-terms-note" className="text-xs text-ink-3">{testMode && <b data-testid="checkout-test-mode" className="text-amber-700">Test mode: no real money is charged. </b>}By paying you agree to the <a href="/legal/terms" target="_blank" className="underline">Terms</a> and <a href="/legal/refunds" target="_blank" className="underline">Refund Policy</a>.</p>}
            {!paymentsEnabled && <p data-testid="checkout-disabled-note" className="text-xs text-ink-3">Online payments aren't live yet. You won't be charged.</p>}
          </div>
        ) : <Loader2 className="mx-auto h-5 w-5 animate-spin text-ink-3" />)}
        {done && <Button data-testid="checkout-close-btn" onClick={onClose} className="rounded-full">Got it</Button>}
      </DialogContent>
    </Dialog>
  );
}
